/**
 * The assay metadata form's values: what the fields hold as typed, how they
 * are read from an assay and written back as attributes, and when two sets
 * of typed values mean the same thing to the API.
 */
import { ASSAY_CAPTURES, type Assay, type AssayCapture } from '@mariana/labos-client';

export interface AssayMetadataValues {
  title: string;
  /** Comma-separated as typed; see parseSampleTypes. */
  sampleTypes: string;
  capture: '' | AssayCapture;
  method: string;
  description: string;
  sopUrl: string;
}

export const parseSampleTypes = (text: string): string[] =>
  Array.from(new Set(text.split(/[,\n]/).map(s => s.trim()).filter(Boolean)));

export function metadataValuesOf(assay: Assay | null | undefined): AssayMetadataValues {
  const a = assay?.attributes ?? {};
  return {
    title: a.title ?? '',
    sampleTypes: Array.isArray(a.sample_types) ? (a.sample_types as unknown[]).filter((s): s is string => typeof s === 'string').join(', ') : '',
    capture: typeof a.capture === 'string' && (ASSAY_CAPTURES as readonly string[]).includes(a.capture) ? (a.capture as AssayCapture) : '',
    method: a.method ?? '',
    description: a.description ?? '',
    sopUrl: a.sop_url ?? '',
  };
}

/** The attributes a PATCH or POST carries for these values; empty text clears an attribute (null). */
export function metadataAttributes(v: AssayMetadataValues, opts: { includeTitle?: boolean; forCreate?: boolean } = {}): Record<string, unknown> {
  const text = (s: string) => (s.trim() ? s.trim() : opts.forCreate ? undefined : null);
  const types = parseSampleTypes(v.sampleTypes);
  const attrs: Record<string, unknown> = {
    capture: v.capture || (opts.forCreate ? undefined : null),
    method: text(v.method),
    description: text(v.description),
    sop_url: text(v.sopUrl),
    sample_types: types.length > 0 ? types : opts.forCreate ? undefined : null,
  };
  if (opts.includeTitle) attrs.title = v.title.trim();
  for (const k of Object.keys(attrs)) if (attrs[k] === undefined) delete attrs[k];
  return attrs;
}

/**
 * True when both sets of values would write the same attributes: the form
 * compares what it holds against what the server last returned this way, so
 * "pls,raffinate" is not an unsaved edit of "pls, raffinate".
 */
export const sameMetadata = (a: AssayMetadataValues, b: AssayMetadataValues): boolean =>
  JSON.stringify(metadataAttributes(a, { includeTitle: true })) === JSON.stringify(metadataAttributes(b, { includeTitle: true }));
